import React from 'react';

import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {
  BiointelligentLinkAnnotation,
  LINKED_ENTITY_CHIP_HORIZONTAL_PADDING,
  PART_LINK_ARROW_COLOR,
  TRANSCRIPTION_ARROW_COLOR,
} from 'molbio/maps/BiointelligentLinksConstants';
import BiointelligentLinkChip from 'molbio/maps/linear/BiointelligentLinkChip';
import {
  getDisplayNameParams,
  getLinkToRnaSequencePathParams,
  getLinkToSequencePathParams,
} from 'molbio/maps/linear/BiointelligentLinksRenderParams';
import Type from 'molbio/maps/Type';

interface BiointelligentLinkAnnotationInnerDetailsProps {
  linkAnnotation: BiointelligentLinkAnnotation;
  sliceStart: number;
  sliceEnd: number;
  type: Type;
  annotationWidth: number;
  annotationHeight: number;
}

/**
 * Renders the 'inner details' of a biointelligent link annotation.
 *
 * Each link has:
 * - a chip with the icon and name of the linked entity
 * - stylized arrows to indicate direction and provide a visual cue for the link type
 */
export default function BiointelligentLinkAnnotationInnerDetails(
  props: BiointelligentLinkAnnotationInnerDetailsProps
): JSX.Element {
  const {linkAnnotation, type, annotationWidth} = props;
  switch (linkAnnotation.linkType) {
    case BiointelligentLinkType.DNA_PART: {
      return <DnaSequencePartLinkArrows {...props} />;
    }
    case BiointelligentLinkType.TRANSCRIPTION: {
      return <TranscriptionPartLinkArrows {...props} />;
    }
    default: {
      // TODO: Implement stylized arrows for other link types.
      const displayNameParams = getDisplayNameParams(
        linkAnnotation.linkType,
        linkAnnotation.name,
        type,
        annotationWidth,
        LINKED_ENTITY_CHIP_HORIZONTAL_PADDING,
        0,
        false
      );
      return (
        <BiointelligentLinkChip
          linkName={displayNameParams.displayName}
          linkIcon={displayNameParams.iconId}
          linkType={linkAnnotation.linkType}
          chipMaxWidth={displayNameParams.width}
          annotationWidth={annotationWidth}
          linkDirection={linkAnnotation.direction}
        />
      );
    }
  }
}
interface DnaSequencePartLinkArrowsProps {
  linkAnnotation: BiointelligentLinkAnnotation;
  sliceStart: number;
  sliceEnd: number;
  type: Type;
  annotationWidth: number;
  annotationHeight: number;
}

/**
 * Renders the long arrows and 'ticks' for a DNA sequence part link.
 *
 * This ends up looking like a stylized representation of DNA base pairs, with two long arrows
 * pointing in the direction of the part link that have little 'ticks' coming off of them towards each other.
 */
function DnaSequencePartLinkArrows({
  linkAnnotation,
  sliceStart,
  sliceEnd,
  type,
  annotationWidth,
  annotationHeight,
}: DnaSequencePartLinkArrowsProps): JSX.Element {
  const {topLineParams, bottomLineParams, dnaTickParams, displayNameParams} = getLinkToSequencePathParams(
    linkAnnotation,
    sliceStart,
    sliceEnd,
    type,
    annotationWidth,
    annotationHeight
  );
  const lineStrokeParams = {
    strokeWidth: 1,
    stroke: PART_LINK_ARROW_COLOR,
    fill: PART_LINK_ARROW_COLOR,
  };
  const {arrowPointD: topArrowPointD, ...topLineDrawParams} = topLineParams;
  const {arrowPointD: bottomArrowPointD, ...bottomLineDrawParams} = bottomLineParams;
  return (
    <>
      <line {...topLineDrawParams} {...lineStrokeParams} />
      <line {...bottomLineDrawParams} {...lineStrokeParams} />
      {topArrowPointD && (
        <path d={topArrowPointD} fill={PART_LINK_ARROW_COLOR} shapeRendering="geometricPrecision" />
      )}
      {bottomArrowPointD && (
        <path d={bottomArrowPointD} fill={PART_LINK_ARROW_COLOR} shapeRendering="geometricPrecision" />
      )}
      {/* For a future optimization, we could use an SVG pattern instead of drawing each line. */}
      {dnaTickParams.map((tickParam, index) => (
        <line key={`${index}-tick`} {...tickParam} {...lineStrokeParams} />
      ))}
      <BiointelligentLinkChip
        linkName={displayNameParams.displayName}
        linkIcon={displayNameParams.iconId}
        linkType={BiointelligentLinkType.DNA_PART}
        annotationWidth={annotationWidth}
        chipMaxWidth={displayNameParams.width}
        linkDirection={linkAnnotation.direction}
      />
    </>
  );
}

interface TranscriptionPartLinkArrowsProps {
  linkAnnotation: BiointelligentLinkAnnotation;
  sliceStart: number;
  sliceEnd: number;
  type: Type;
  annotationWidth: number;
  annotationHeight: number;
}

/**
 * Renders the stylized arrows for a transcription part link.
 *
 * This ends up looking like a stylized representation of RNA residues, with a single long arrow
 * pointing in the direction of the part link that has little 'ticks' coming off of it.
 */
function TranscriptionPartLinkArrows({
  linkAnnotation,
  sliceStart,
  sliceEnd,
  type,
  annotationWidth,
  annotationHeight,
}: TranscriptionPartLinkArrowsProps): JSX.Element {
  const {lineParams, arrowPointD, rnaTickParams, displayNameParams} = getLinkToRnaSequencePathParams(
    linkAnnotation,
    sliceStart,
    sliceEnd,
    type,
    annotationWidth,
    annotationHeight
  );
  const lineStrokeParams = {
    strokeWidth: 1,
    stroke: TRANSCRIPTION_ARROW_COLOR,
    fill: TRANSCRIPTION_ARROW_COLOR,
  };
  return (
    <>
      <line {...lineParams} {...lineStrokeParams} />
      {arrowPointD && (
        <path d={arrowPointD} fill={TRANSCRIPTION_ARROW_COLOR} shapeRendering="geometricPrecision" />
      )}
      {/* For a future optimization, we could use an SVG pattern instead of drawing each line. */}
      {rnaTickParams.map((tickParam, index) => (
        <line key={`${index}-tick`} {...tickParam} {...lineStrokeParams} />
      ))}
      <BiointelligentLinkChip
        linkName={displayNameParams.displayName}
        linkIcon={displayNameParams.iconId}
        linkType={BiointelligentLinkType.TRANSCRIPTION}
        annotationWidth={annotationWidth}
        chipMaxWidth={displayNameParams.width}
        linkDirection={linkAnnotation.direction}
      />
    </>
  );
}
